import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Icon, Reveal } from "./common";
import { CONTACT, whatsappLink, IMG } from "../data/site";
import { api } from "../lib/api";

export const SectionHead = ({ eyebrow, title, sub, light, center }) => (
  <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl`}>
    {eyebrow && (
      <Reveal>
        <span className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] ${light ? "text-blue" : "text-blue"}`}>
          <span className="h-px w-8 bg-blue" />{eyebrow}
        </span>
      </Reveal>
    )}
    <Reveal delay={0.05}>
      <h2 className={`mt-4 font-display text-3xl md:text-5xl leading-[1.05] ${light ? "text-white" : "text-navy"}`}>{title}</h2>
    </Reveal>
    {sub && <Reveal delay={0.1}><p className={`mt-4 text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-slate-500"}`}>{sub}</p></Reveal>}
  </div>
);

export const HeroButtons = ({ onLight }) => {
  const { t } = useLang();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Link to="/quote" data-testid="cta-quote" className="inline-flex items-center gap-2 rounded-full bg-blue px-7 py-4 text-sm font-semibold text-white hover:bg-[#0052FF] transition-colors shadow-lg shadow-blue/30">
        {t.cta.quoteFree} <Icon name="ArrowRight" size={17} className="rtl:rotate-180" />
      </Link>
      <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-4 text-sm font-semibold text-white hover:brightness-95 transition">
        <Icon name="MessageCircle" size={17} /> {t.cta.whatsapp}
      </a>
      <a href={`tel:${CONTACT.phoneRaw}`} data-testid="cta-call" className={`inline-flex items-center gap-2 rounded-full px-6 py-4 text-sm font-semibold transition border ${onLight ? "border-navy/15 text-navy hover:bg-navy hover:text-white" : "border-white/25 text-white hover:bg-white hover:text-navy"}`}>
        <Icon name="Phone" size={17} /> {t.cta.call}
      </a>
    </div>
  );
};

export const CTABand = () => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden bg-navy grain py-20 md:py-28" data-testid="cta-band">
      <div className="absolute -top-24 -end-24 h-80 w-80 rounded-full bg-blue/30 blur-3xl" />
      <div className="relative z-10 mx-auto max-w-4xl px-5 text-center">
        <SectionHead center light eyebrow="Sparko" title={t.sections.ctaTitle} sub={t.sections.ctaSub} />
        <Reveal delay={0.15}><div className="mt-8 flex justify-center"><HeroButtons /></div></Reveal>
      </div>
    </section>
  );
};

// Draggable before/after comparison slider
export const BeforeAfter = ({ before = IMG.officeBefore, after = IMG.officeAfter, className = "" }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const move = (clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100)));
  };
  return (
    <div ref={ref} className={`relative select-none overflow-hidden rounded-3xl ${className}`} data-testid="before-after"
      onMouseMove={(e) => e.buttons === 1 && move(e.clientX)}
      onTouchMove={(e) => move(e.touches[0].clientX)}>
      <img src={after} alt="After" className="absolute inset-0 h-full w-full object-cover" draggable="false" />
      <img src={before} alt="Before" className="absolute inset-0 h-full w-full object-cover grayscale" draggable="false"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
      <span className="absolute top-4 start-4 z-10 rounded-full bg-navy/80 px-3 py-1 text-xs font-semibold text-white">Before</span>
      <span className="absolute top-4 end-4 z-10 rounded-full bg-blue px-3 py-1 text-xs font-semibold text-white">After</span>
      <div className="absolute inset-y-0 z-10 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ms-px w-0.5 bg-white" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 grid place-items-center h-11 w-11 rounded-full bg-white shadow-lg">
          <Icon name="MoveHorizontal" size={18} className="text-navy" />
        </div>
      </div>
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(+e.target.value)}
        className="absolute inset-0 z-20 w-full h-full opacity-0 cursor-ew-resize" aria-label="Comparison slider" data-testid="ba-range" />
    </div>
  );
};

// AI-powered service finder
export const AISearch = () => {
  const { t, lang } = useLang();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const ask = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setRes(null);
    try {
      const { data } = await api.post("/ai/search", { query: q, lang });
      setRes(data);
    } catch { setRes({ answer: t.form.error, matches: [] }); }
    setLoading(false);
  };
  return (
    <div className="rounded-3xl glass-dark p-6 md:p-10" data-testid="ai-search">
      <div className="flex items-center gap-3 text-blue">
        <div className="grid place-items-center h-11 w-11 rounded-full bg-blue/20"><Icon name="Sparkles" size={20} /></div>
        <div>
          <h3 className="font-display text-xl text-white">{t.sections.aiTitle}</h3>
          <p className="text-sm text-white/60">{t.sections.aiSub}</p>
        </div>
      </div>
      <form onSubmit={ask} className="mt-5 flex flex-col sm:flex-row gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.sections.aiPlaceholder} data-testid="ai-input"
          className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3.5 text-white placeholder-white/40 outline-none focus:border-blue" />
        <button type="submit" disabled={loading} data-testid="ai-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blue px-6 py-3.5 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
          {loading ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Search" size={18} />}
          {t.cta.explore}
        </button>
      </form>
      {res && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 rounded-2xl bg-white/5 p-5">
          <p className="text-white/85 leading-relaxed">{res.answer}</p>
          {res.matches?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {res.matches.map((m) => (
                <Link key={m} to={`/services/${m.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="rounded-full bg-blue/20 text-blue px-3 py-1.5 text-xs font-medium hover:bg-blue/30">{m}</Link>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};
